import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { dateToLocalFormat } from 'helpers/dateToLocalFormat';
import ErrorState from 'common/ErrorState/ErrorState';
import BackButton from 'common/BackButton/BackButton';
import { useArchive } from 'hooks/useArchive';
import Loader from 'components/Loader/Loader';
import './Archive.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Архів заходів — ADR-0012.
 *
 * Свідомо не повторює розкладку календаря. Календар — це вітрина найближчого:
 * великі картки, по місяцях. Архів — реєстр: щільні рядки, згруповані за
 * роками, читаються згори вниз як покажчик. Тому картку заходу тут не
 * перевикористано.
 *
 * Довантаження при прокручуванні зроблено спостерігачем за маячком у кінці
 * списку — тим самим прийомом, що вже стоїть у каталогах. Слухача прокрутки
 * тут немає: він рахував би висоти на кожному кадрі (ADR-0008).
 *
 * Звідки беруться записи й як зводяться дві незалежні нумерації сторінок —
 * справа useArchive; сторінка отримує готовий перелік.
 */
export default function Archive() {
  const language = useLanguage();
  const { events, status, error, total, hasMore, partial, loadMore, retry } = useArchive();

  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || status !== 'succeeded') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '400px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore, status]);

  const years = groupByYear(events);

  return (
    <article className='archive section'>
      <div className='container'>
        <BackButton />

        <header className='archive-head'>
          <h1 className='page-title'>{language === 'en' ? 'Past events' : 'Заходи, що відбулися'}</h1>
          <p className='lede'>
            {language === 'en'
              ? 'A register of competitions, meetings and professional training that have already taken place.'
              : 'Реєстр змагань, зборів і професійного навчання, які вже відбулися.'}
          </p>
          {status === 'succeeded' && total > 0 && (
            <p className='archive-total tnum'>
              {total} {language === 'en' ? 'entries' : 'записів'}
            </p>
          )}
        </header>

        {status === 'succeeded' && partial && (
          <p className='calendar-partial'>
            {partial === 'competitions'
              ? language === 'en'
                ? 'Competitions from e-phan are temporarily unavailable — the archive shows only SGOSTU events.'
                : 'Змагання з e-phan тимчасово недоступні — в архіві лише заходи СГОСТУ.'
              : language === 'en'
              ? 'SGOSTU events are temporarily unavailable — the archive shows only competitions from e-phan.'
              : 'Заходи СГОСТУ тимчасово недоступні — в архіві лише змагання з e-phan.'}
          </p>
        )}

        {status === 'loading' && <Loader />}

        {status === 'failed' && events.length === 0 && (
          <ErrorState error={error} onRetry={retry} />
        )}

        {events.length > 0 && (
          <div className='archive-years'>
            {years.map(({ year, items }) => (
              <section className='archive-year' key={year}>
                <h2 className='archive-year-title tnum'>{year}</h2>

                <ul className='archive-list'>
                  {items.map((event) => (
                    <ArchiveRow event={event} language={language} key={event.id} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        {status === 'succeeded' && events.length === 0 && (
          <p className='empty-note'>
            {language === 'en' ? 'The archive is empty so far.' : 'Архів поки порожній.'}
          </p>
        )}

        {/* Збій довантаження не має стирати вже показане */}
        {status === 'failed' && events.length > 0 && <ErrorState error={error} onRetry={retry} />}

        {status === 'loadingMore' && (
          <p className='archive-more' role='status'>
            {language === 'en' ? 'Loading more' : 'Завантажую ще'}
          </p>
        )}

        {hasMore && status !== 'failed' && <span ref={sentinelRef} className='archive-sentinel' aria-hidden='true' />}

        {!hasMore && events.length > 0 && status === 'succeeded' && (
          <p className='archive-end'>{language === 'en' ? 'That is the whole archive.' : 'Це весь архів.'}</p>
        )}
      </div>
    </article>
  );
}

function ArchiveRow({ event, language }) {
  const dates = event.end
    ? `${dateToLocalFormat(event.start).slice(0, 5)} - ${dateToLocalFormat(event.end).slice(0, 5)}`
    : dateToLocalFormat(event.start).slice(0, 5);

  return (
    <li className='archive-item'>
      <Link className='archive-link' to={`/calendar/${event.slug}`}>
        <span className='archive-date tnum'>{dates}</span>

        <span className='archive-main'>
          <span className='archive-title'>{event.title}</span>
          <span className='archive-meta'>
            {[event.town, event.type].filter(Boolean).join(' / ')}
          </span>
        </span>

        <span className='archive-badges'>
          {event.foreign && (
            <span className='archive-foreign'>{language === 'en' ? 'International' : 'Міжнародний'}</span>
          )}
          {event.source === 'ephan' && <span className='archive-source'>e-phan</span>}
        </span>

        <svg className='archive-arrow' width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
          <path
            d='M5 12h14M13 6l6 6-6 6'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </Link>
    </li>
  );
}

/** Заходи по роках, від найсвіжішого. Порядок усередині зберігає сервер. */
function groupByYear(events) {
  const groups = [];

  for (const event of events) {
    const year = String(event.start).slice(0, 4);
    const last = groups[groups.length - 1];

    if (last && last.year === year) last.items.push(event);
    else groups.push({ year, items: [event] });
  }

  return groups;
}
